import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import caseServicesService from '../../../services/caseServicesService';

const formatMoney = (amount, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'EUR' }).format(
      Number(amount) || 0,
    );
  } catch {
    return `${Number(amount).toFixed(2)} ${currency || ''}`;
  }
};

export default function CaseOrderServicesSummary({ caseId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!caseId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await caseServicesService.listByCaseId(caseId);
        if (!cancelled) setRows(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Chargement des services commande…
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
        Aucun service sélectionné pour cette commande.
      </div>
    );
  }

  const currency = rows[0]?.currencyCode || 'EUR';
  const total = rows.reduce((s, r) => s + r.lineTotal, 0);

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Icon name="Briefcase" size={18} className="text-primary shrink-0" />
        <h3 className="text-sm font-semibold text-foreground">Services de la commande</h3>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-3 text-sm border-b border-border/60 pb-2 last:border-0 last:pb-0"
          >
            <span className="text-foreground">
              {r.serviceName}
              {r.quantity > 1 ? ` × ${r.quantity}` : ''}
            </span>
            <span className="font-medium text-primary shrink-0">
              {formatMoney(r.lineTotal, r.currencyCode || currency)}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground pt-1 border-t border-border">
        Total services (facturés sur la première facture de production) :{' '}
        <span className="font-semibold text-foreground">{formatMoney(total, currency)}</span>
      </p>
    </div>
  );
}
